import { useLocation } from "react-router-dom";
import pages from "./pages.json";
import { TRANSLATED_PATHS, basePath, pathForLang } from "../i18n/lang";
import PRESS from "../data/press";
import FAQ from "../data/faq";
import {
  useSeo,
  breadcrumbJsonLd,
  SITE_URL,
  ORGANIZATION_ID,
} from "./useSeo";

const normalizePath = (pathname) =>
  pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;

// Metadate pentru paginile fixe (definite în pages.json).
// Articolele de blog și pagina 404 își setează singure metadatele.
const RouteSeo = () => {
  const { pathname } = useLocation();
  const path = normalizePath(pathname);
  const page = pages[path];

  let seo = null;
  if (page) {
    const canonicalPath = page.canonical || path;
    const url = `${SITE_URL}${canonicalPath}`;
    const jsonLd = [
      {
        "@type": page.schemaType || "WebPage",
        "@id": `${url}#webpage`,
        url,
        name: page.title,
        description: page.description,
        inLanguage: page.lang,
        isPartOf: { "@id": `${SITE_URL}/#website` },
        about: { "@id": ORGANIZATION_ID },
      },
    ];
    const homePath = page.lang === "ro" ? "/ro" : "/";
    if (canonicalPath !== homePath) {
      const crumbs = [{ name: "Travel Time", path: homePath }];
      if (page.parent && pages[page.parent]) {
        crumbs.push({ name: pages[page.parent].name, path: page.parent });
      }
      crumbs.push({ name: page.name, path: canonicalPath });
      jsonLd.push(breadcrumbJsonLd(crumbs));
    }
    // Pagina de presă: articolele independente despre firmă, ca NewsArticle
    if (page.jsonLdExtra === "press") {
      jsonLd.push({
        "@type": "ItemList",
        name: page.name,
        itemListElement: PRESS.map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          item: {
            "@type": "NewsArticle",
            headline: item.title,
            url: item.url,
            datePublished: item.date,
            inLanguage: "ro",
            description: item.summary[page.lang],
            publisher: { "@type": "Organization", name: item.publisher, url: item.publisherUrl },
            ...(item.author ? { author: { "@type": "Person", name: item.author } } : {}),
            about: { "@id": ORGANIZATION_ID },
          },
        })),
      });
    }

    // Pagina FAQ: întrebările și răspunsurile ca FAQPage
    if (page.jsonLdExtra === "faq") {
      jsonLd[0].mainEntity = FAQ.flatMap((section) =>
        section.items.map((item) => ({
          "@type": "Question",
          name: item.q[page.lang],
          acceptedAnswer: { "@type": "Answer", text: item.a[page.lang] },
        }))
      );
    }

    // Paginile traduse: legături hreflang către ambele variante (engleza e implicită)
    const base = basePath(canonicalPath);
    const alternates = TRANSLATED_PATHS.includes(base)
      ? [
          { hreflang: "en", path: pathForLang(base, "en") },
          { hreflang: "ro", path: pathForLang(base, "ro") },
          { hreflang: "x-default", path: pathForLang(base, "en") },
        ]
      : null;

    seo = {
      title: page.title,
      description: page.description,
      lang: page.lang,
      path: canonicalPath,
      jsonLd,
      alternates,
    };
  }

  useSeo(seo);
  return null;
};

export default RouteSeo;
